import { z } from 'zod'

/**
 * Search params accepted by the sign-in page, enabling SSO deep-links such as
 * `/sign-in?ssoIdentifier=acme.com&autoSubmit=true`.
 *
 * - `ssoIdentifier` is the value used to resolve the SSO config (a domain or an
 *   email). Its presence opens the SSO form straight away.
 * - `autoSubmit=true` (with an `ssoIdentifier`) fires the SSO redirect on load.
 *
 * Invalid values are dropped (`.catch`) so a malformed link degrades to the
 * standard sign-in screen rather than crashing the route.
 */
export const signInSearchSchema = z.object({
  ssoIdentifier: z.string().trim().min(1).optional().catch(undefined),
  autoSubmit: z.boolean().optional().catch(undefined),
})

export type SignInSearch = z.infer<typeof signInSearchSchema>

export interface SignInScreenSsoSearchProps {
  defaultMode?: 'sso'
  ssoIdentifier?: string
  autoSubmitSso: boolean
}

/** Maps validated search params to the SSO props consumed by `SignInScreen`. */
export function ssoPropsFromSearch(
  search: SignInSearch
): SignInScreenSsoSearchProps {
  return {
    defaultMode: search.ssoIdentifier ? 'sso' : undefined,
    ssoIdentifier: search.ssoIdentifier,
    autoSubmitSso: Boolean(search.autoSubmit && search.ssoIdentifier),
  }
}
